import { ReactElement } from 'react';
import { formatNumber } from '../breakdownFormat';
import FormulaRow from './FormulaRow';

interface Props {
  alphaPower: number;
  cdaAdjusted: number;
  aeroPower: number;
  aeroWork: number;
  duration: number;
}

export default function AeroFromCdaSection({
  alphaPower,
  cdaAdjusted,
  aeroPower,
  aeroWork,
  duration,
}: Props): ReactElement {
  return (
    <div className="cr-py-2">
      <p className="cr-text-sm cr-text-gray-500 cr-mb-4">
        Aero power predicted by the run&rsquo;s adjusted CdA. Used as an input to the energy
        balance instead of being solved from it (the inverse of the CdA view).
      </p>

      <FormulaRow
        className="cr-mb-4"
        inputs={[
          {
            header: 'Alpha (Alpha_PG3, per traverse)',
            value: Number.isFinite(alphaPower)
              ? `${formatNumber(alphaPower, 1)} W`
              : 'Alpha_PG3 missing',
          },
          {
            header: '× Adjusted CdA (run)',
            value: Number.isFinite(cdaAdjusted)
              ? `${formatNumber(cdaAdjusted, 4)} m²`
              : 'CdA missing',
          },
        ]}
        result={{
          header: 'Aero power',
          value: Number.isFinite(aeroPower) ? `${formatNumber(aeroPower, 1)} W` : '—',
        }}
      />
      <FormulaRow
        inputs={[
          {
            header: 'Aero power',
            value: Number.isFinite(aeroPower) ? `${formatNumber(aeroPower, 1)} W` : '—',
          },
          { header: '× Time', value: `${formatNumber(duration, 2)} s` },
        ]}
        result={{
          header: 'Aero work',
          value: Number.isFinite(aeroWork) ? `${formatNumber(aeroWork, 0)} J` : '—',
        }}
      />

      <p className="cr-text-xs cr-text-gray-500 cr-mt-3 cr-italic">
        Adjusted CdA = <code>config.cda_ref</code> + per-traverse adjustments. Both Alpha and
        Adjusted CdA vary per traverse, so Aero power reflects this traverse&rsquo;s actual
        airspeed, yaw, and body position rather than a run-level approximation.
      </p>
    </div>
  );
}
